import { faBell } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFileRoute } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import ConfirmSmsOptOutModal from 'src/components/modals/ConfirmOptOutModal';
import { useModalManager } from 'src/components/modals/ModalManager';
import { useOnboardingWorkflow } from 'src/contexts/OnboardingWorkflowContext';
import { setHasConsentedToSms } from 'src/store/slices/onboardingSlice';
import { useAppDispatch } from 'src/store/store';

export const Route = createFileRoute('/(app)/onboarding/enable-notifications')({
    component: EnableNotifications,
});

function EnableNotifications() {
    const dispatch = useAppDispatch();

    const { palette } = useTheme();
    const { openModal } = useModalManager();

    const { progress, previousStep, canGoBack, nextStep, isProcessing } = useOnboardingWorkflow();

    const handleUpdateSmsOptIn = (isOptedIn: boolean) => {
        dispatch(setHasConsentedToSms(isOptedIn));
        nextStep();
    };

    const handleConfirmSmsOptOut = () => {
        openModal({
            content: ConfirmSmsOptOutModal,
            props: {
                content:
                    "You won't receive any notifications. Without SMS notifications, you may miss important information.",
                onConfirm: () => {
                    handleUpdateSmsOptIn(false);
                },
            },
        });
    };

    return (
        <>
            <PageHeader
                hideBackButton={!canGoBack}
                onBack={canGoBack ? previousStep : undefined}
                hideActionButton
            >
                <StyledLinearProgressBar value={progress.percentage} />
            </PageHeader>

            <Stack sx={{ py: 2.5, gap: 1 }}>
                <Box sx={{ py: 1 }}>
                    <FontAwesomeIcon icon={faBell} size="3x" color={palette.primary.main} />
                </Box>

                <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                    Real time notifications
                </Typography>

                <Typography variant="body2">
                    Sage Home will let you know in real time when something needs your attention.
                    <br />
                    <br />
                    By opting-in to SMS notifications, you agree to receive text notifications from
                    Sage Home to help manage your caregiving journey. You may opt out at any time by
                    texting STOP. Message & data rates may apply.
                </Typography>
            </Stack>
            <PageFooter>
                <Button
                    variant="text"
                    color="primary"
                    fullWidth
                    disabled={isProcessing}
                    onClick={handleConfirmSmsOptOut}
                >
                    Turn off SMS Notifications
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    loading={isProcessing}
                    onClick={() => handleUpdateSmsOptIn(true)}
                >
                    Opt-in to SMS notifications
                </Button>
            </PageFooter>
        </>
    );
}
